import { useState } from 'react';
import { X, Plus, User, Trash2 } from 'lucide-react';
import { useSignersModal } from '../../hooks/use-signers-modal.hook';
import { Signer } from '../../types/signers';
import axios from 'axios';

interface SignersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  userShortId: string;
  mappingShortId: string;
  recordId: string;
  initialSigners: Signer[];
}

// Purpose: Modal component for managing multiple signers with email-based tracking
export const SignersModal = ({
  isOpen,
  onClose,
  onConfirm,
  userShortId,
  mappingShortId,
  recordId,
  initialSigners,
}: SignersModalProps) => {
  const [isNotifying, setIsNotifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    signers,
    addSigner,
    removeSigner,
    updateSignerEmail,
    isSubmissionAllowed,
    submitSigners,
    isLoading,
  } = useSignersModal({ isOpen, userShortId, mappingShortId, recordId, initialSigners });

  const handleModalSubmit = async () => {
    if (isLoading || isNotifying) return;

    let success = false;
    try {
      success = await submitSigners();
      if (!success) return;

      if (onConfirm) {
        setIsNotifying(true);
        await onConfirm();
      }
      success = true;
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        error.response?.data?.error === 'duplicate_signer'
      ) {
        setErrorMessage('This email is already registered as a signer for this application.');
      } else {
        setErrorMessage('Failed to update signers. Please try again.');
      }
    } finally {
      setIsNotifying(false);
      if (success) {
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="signers-modal"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Application Signers</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Additional Signers */}
          <div className="pt-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <User className="h-5 w-5 text-green-500" />
              Additional Signers
            </label>
            <p className="text-gray-600 mb-3 text-sm">
              Please add email addresses for any additional owners who need to sign this
              application.
            </p>

            <div className="space-y-3">
              {signers.map((signer) => (
                <div key={signer.signer_id} className="flex gap-2">
                  <input
                    type="email"
                    value={signer.email || ''}
                    onChange={(e) => updateSignerEmail(signer.signer_id, e.target.value)}
                    placeholder="owner@example.com"
                    className="flex-1 px-3 py-2 border rounded-md text-gray-900 placeholder-gray-500 bg-white"
                  />
                  <button
                    onClick={() => removeSigner(signer.signer_id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={addSigner}
              className="mt-4 flex items-center text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Another Signer
            </button>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mt-4">
            <div className="text-red-800 text-sm font-medium">{errorMessage}</div>
          </div>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-700">
            Cancel
          </button>
          <button
            onClick={handleModalSubmit}
            disabled={!isSubmissionAllowed || isLoading || isNotifying}
            data-testid="signers-modal-confirm-button"
            className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
              !isSubmissionAllowed || isLoading || isNotifying
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
            {isLoading || isNotifying ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};
